import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatMXN } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Carrito() {
  const { items, removeItem, setQty, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMeta, setCouponMeta] = useState(null);
  const [shipping, setShipping] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    country: "México",
    notes: "",
  });
  const [placing, setPlacing] = useState(false);

  const shippingFee = (subtotal - discount) >= 999 ? 0 : 99;
  const total = Math.max(0, subtotal - discount + shippingFee);

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setDiscount(data.discount);
      setCouponMeta(data);
      toast.success(`Cupón aplicado: -${formatMXN(data.discount)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cupón no válido");
    }
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (!shipping.full_name || !shipping.email || !shipping.phone || !shipping.address || !shipping.city) {
      return toast.error("Completa tus datos de envío");
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((it) => ({ product_id: it.product_id, qty: it.qty, size: it.size, color: it.color })),
        shipping,
        coupon_code: couponMeta?.code || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos iniciar el pago");
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center" data-testid="empty-cart">
        <h1 className="font-serif text-5xl tracking-tighter">Tu bolsa está vacía</h1>
        <p className="mt-4 text-[#5B605E]">Encuentra algo bonito que te haga sonreír.</p>
        <Link to="/catalogo" className="btn-primary mt-8 inline-flex">Explorar catálogo</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-12 py-12 grid lg:grid-cols-12 gap-12" data-testid="cart-page">
      <div className="lg:col-span-7">
        <h1 className="font-serif text-5xl tracking-tighter mb-8">Tu bolsa</h1>

        <div className="space-y-6">
          {items.map((it) => (
            <div key={it.key} className="flex gap-4 border-b border-[#E8E3DA] pb-6">
              <img src={it.image} alt={it.name} className="h-32 w-24 object-cover" />
              <div className="flex-1">
                <Link to={`/producto/${it.slug}`} className="font-serif text-xl">{it.name}</Link>
                {(it.size || it.color) && <p className="text-xs text-[#5B605E] mt-1">{[it.size, it.color].filter(Boolean).join(" · ")}</p>}
                <div className="mt-3 flex items-center gap-4">
                  <div className="flex items-center border border-[#2C302E]">
                    <button onClick={() => setQty(it.key, it.qty - 1)} className="px-2 py-1">−</button>
                    <span className="px-3 text-sm">{it.qty}</span>
                    <button onClick={() => setQty(it.key, it.qty + 1)} className="px-2 py-1">+</button>
                  </div>
                  <button onClick={() => removeItem(it.key)} className="text-xs text-[#5B605E] link-underline">Quitar</button>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">{formatMXN(it.price * it.qty)}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-serif text-3xl tracking-tighter mt-12 mb-4">Datos de envío</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            ["full_name", "Nombre completo"],
            ["email", "Correo"],
            ["phone", "Teléfono"],
            ["city", "Ciudad"],
          ].map(([k, label]) => (
            <input key={k} placeholder={label} value={shipping[k]} onChange={(e) => setShipping({ ...shipping, [k]: e.target.value })}
              className="bg-transparent border border-[#2C302E] px-4 py-3 text-sm focus:outline-none focus:border-[#A94438]"
              data-testid={`shipping-${k}`} />
          ))}
          <input placeholder="Dirección" value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })}
            className="sm:col-span-2 bg-transparent border border-[#2C302E] px-4 py-3 text-sm focus:outline-none focus:border-[#A94438]" data-testid="shipping-address" />
          <textarea placeholder="Notas (opcional)" value={shipping.notes} onChange={(e) => setShipping({ ...shipping, notes: e.target.value })}
            className="sm:col-span-2 bg-transparent border border-[#2C302E] px-4 py-3 text-sm focus:outline-none focus:border-[#A94438]" rows={3} data-testid="shipping-notes" />
        </div>
      </div>

      <aside className="lg:col-span-5">
        <div className="bg-[#F2EFE9] p-8 sticky top-24">
          <h2 className="font-serif text-3xl tracking-tighter">Resumen</h2>

          <div className="mt-6 flex gap-2">
            <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Código de cupón"
              className="flex-1 bg-transparent border border-[#2C302E] px-4 py-3 text-sm focus:outline-none"
              data-testid="coupon-input" />
            <button onClick={applyCoupon} className="btn-ghost" data-testid="apply-coupon">Aplicar</button>
          </div>

          <div className="mt-6 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">{formatMXN(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-[#A94438]"><span>Descuento</span><span data-testid="summary-discount">−{formatMXN(discount)}</span></div>}
            <div className="flex justify-between"><span>Envío</span><span>{shippingFee === 0 ? "Gratis" : formatMXN(shippingFee)}</span></div>
            <div className="border-t border-[#2C302E]/30 pt-2 mt-2 flex justify-between font-medium text-base"><span>Total</span><span data-testid="summary-total">{formatMXN(total)}</span></div>
          </div>

          <button onClick={checkout} disabled={placing} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="checkout-btn">
            {placing ? "Redirigiendo…" : "Pagar con Stripe →"}
          </button>
          <p className="mt-3 text-xs text-[#5B605E]">Pago seguro · Stripe (modo de prueba)</p>
        </div>
      </aside>
    </div>
  );
}
